import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Radar, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { INQUIRY_STATUS_COLORS, INQUIRY_STATUS_LABELS, MOON_STATUS_LABELS } from "./config";

const MOON_BADGES = { nouvelle: "bg-slate-100 text-slate-700", en_analyse: "bg-badge text-sky-700", recherche_fournisseur: "bg-badge text-sky-700", devis_recu: "bg-emerald-50 text-emerald-700", devis_envoye: "bg-badge text-sky-700", acceptee: "bg-emerald-50 text-emerald-700", commande_en_cours: "bg-badge text-sky-700", livree: "bg-emerald-50 text-emerald-700", annulee: "bg-red-50 text-red-700" };

export default function MoonRequestsPanel({ lead, onChanged }) {
  const [reqs, setReqs] = useState(null);
  const [showConvert, setShowConvert] = useState(false);
  const [form, setForm] = useState({ product_description: lead.identified_need || "", request_type: "achat", quantity: "" });
  const [openReq, setOpenReq] = useState(null);
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [delayDays, setDelayDays] = useState(5);
  const [delayInput, setDelayInput] = useState("5");
  const [followUps, setFollowUps] = useState({});
  const [editInquiry, setEditInquiry] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => {
    api.get(`/crm/leads/${lead.id}/moon-requests`).then(({ data }) => setReqs(data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/crm/settings").then(({ data }) => { setDelayDays(data.follow_up_delay_days); setDelayInput(String(data.follow_up_delay_days)); }).catch(() => {});
    api.get("/crm/follow-ups").then(({ data }) => {
      const map = {};
      (data.alerts || []).forEach((a) => { map[a.inquiry_id] = a.days_waiting; });
      setFollowUps(map);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [lead.id]);

  const flash = (m) => { setNotice(m); setError(""); load(); onChanged(); setTimeout(() => setNotice(""), 8000); };

  const convert = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/moon-requests`, { ...form, quantity: form.quantity ? Number(form.quantity) : null });
      setShowConvert(false);
      flash(`Demande de prix ${data.request_number} créée — vous pouvez maintenant solliciter des fournisseurs.`);
      loadMatches(data.request_number);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const loadMatches = async (requestNumber) => {
    if (openReq === requestNumber) { setOpenReq(null); return; }
    setOpenReq(requestNumber);
    setMatches(null);
    try {
      const { data } = await api.post("/admin/match-suppliers", { request_number: requestNumber });
      setMatches(data);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); setMatches({ matches: [], manual_search_needed: true, inferred_categories: [] }); }
  };

  const solicit = async (requestNumber, supplierId) => {
    setError("");
    try {
      const { data } = await api.post("/admin/supplier-inquiries", { request_number: requestNumber, supplier_ids: [supplierId] });
      if (data.created?.length) flash(`Fournisseur sollicité — la demande de prix attend votre validation humaine avant envoi (statut « en attente de validation »).`);
      load();
      if (matches) { const { data: m } = await api.post("/admin/match-suppliers", { request_number: requestNumber }); setMatches(m); }
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveDelay = async () => {
    setError("");
    try {
      const { data } = await api.patch("/crm/settings", { follow_up_delay_days: Number(delayInput) });
      setDelayDays(data.follow_up_delay_days);
      flash(`Délai de relance réglé à ${data.follow_up_delay_days} jour(s) sans réponse.`);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openEdit = (inq) => {
    setEditInquiry(inq);
    setEditForm({
      price: inq.price || "", currency: inq.currency || "CAD", lead_time: inq.lead_time || "",
      availability: inq.availability || "", shipping_cost: inq.shipping_cost || "",
      offer_validity: inq.offer_validity || "", comparison_notes: inq.comparison_notes || "",
      follow_up_due: inq.follow_up_due || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.patch(`/crm/inquiries/${editInquiry.id}`, editForm);
      setEditInquiry(null);
      flash("Réponse fournisseur mise à jour (auteur, date et historique enregistrés).");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const markFollowUpDone = async (inq) => {
    setError("");
    try {
      await api.post(`/crm/inquiries/${inq.id}/follow-up-done`, { note: "" });
      flash(`Relance marquée comme effectuée — ${inq.supplier_name}.`);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <section className="rounded-2xl border border-line bg-white p-5" data-testid="crm-moon-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-steel"><Radar size={15} /> Demandes de prix MOON Supply</h3>
        <button onClick={() => setShowConvert((s) => !s)} data-testid="crm-moon-convert-toggle"
          className="rounded-full bg-electric px-4 py-2 text-xs font-bold text-white hover:bg-electric-dark">
          {showConvert ? "Annuler" : "Convertir le besoin en demande de prix"}
        </button>
      </div>
      <p className="mt-2 text-xs text-steel">Réutilise le répertoire fournisseurs MOON (catégories, marques, régions desservies). Les demandes de prix ne contiennent jamais les coordonnées du client et nécessitent votre validation avant tout envoi.</p>

      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-paper px-4 py-3 text-xs text-graphite" data-testid="crm-followup-settings">
        <span className="font-semibold">Rappels internes : relance suggérée après <span className="text-navy-900">{delayDays} jour(s)</span> sans réponse.</span>
        <span className="text-steel">Aucun courriel automatique — alerte affichée ici et au tableau de bord seulement.</span>
        <details className="w-full">
          <summary className="cursor-pointer font-semibold text-steel hover:text-electric">Modifier le délai</summary>
          <div className="mt-2 flex items-center gap-2">
            <input type="number" min="1" max="60" value={delayInput} onChange={(e) => setDelayInput(e.target.value)} data-testid="crm-delay-input"
              className="w-20 rounded-xl border border-line bg-white px-3 py-2 text-sm" />
            <button onClick={saveDelay} data-testid="crm-delay-save" className="rounded-xl bg-navy-800 px-4 py-2 text-xs font-bold text-white hover:bg-navy-900">Enregistrer</button>
          </div>
        </details>
      </div>

      {showConvert && (
        <form onSubmit={convert} className="mt-4 space-y-3 rounded-xl bg-paper p-4" data-testid="crm-moon-convert-form">
          <div>
            <label htmlFor="moon-desc" className="mb-1 block text-[12px] font-bold text-navy-900">Produit / besoin recherché *</label>
            <textarea id="moon-desc" required rows={2} data-testid="crm-moon-product" value={form.product_description}
              onChange={(e) => setForm((s) => ({ ...s, product_description: e.target.value }))}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm" />
          </div>
          <div className="flex flex-wrap gap-3">
            <div>
              <label htmlFor="moon-type" className="mb-1 block text-[12px] font-bold text-navy-900">Type</label>
              <select id="moon-type" data-testid="crm-moon-type" value={form.request_type} onChange={(e) => setForm((s) => ({ ...s, request_type: e.target.value }))}
                className="rounded-xl border border-line bg-white px-3 py-2 text-sm">
                <option value="achat">Achat</option><option value="location">Location</option><option value="piece_rare">Pièce rare</option>
              </select>
            </div>
            <div>
              <label htmlFor="moon-qty" className="mb-1 block text-[12px] font-bold text-navy-900">Quantité</label>
              <input id="moon-qty" type="number" min="1" data-testid="crm-moon-quantity" value={form.quantity}
                onChange={(e) => setForm((s) => ({ ...s, quantity: e.target.value }))} className="w-24 rounded-xl border border-line bg-white px-3 py-2 text-sm" />
            </div>
          </div>
          <button type="submit" data-testid="crm-moon-convert-submit" className="rounded-full bg-navy-800 px-5 py-2 text-xs font-bold text-white hover:bg-navy-900">Créer la demande de prix</button>
        </form>
      )}

      {notice && <p className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700" data-testid="crm-moon-notice">{notice}</p>}
      {error && <p role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="crm-moon-error">{error}</p>}

      {reqs === null ? (
        <p className="mt-4 text-xs text-steel">Chargement…</p>
      ) : reqs.length === 0 ? (
        <p className="mt-4 text-xs text-steel" data-testid="crm-moon-empty">Aucune demande de prix liée à ce lead pour le moment.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {reqs.map((r) => (
            <div key={r.id} className="rounded-xl border border-line" data-testid={`crm-moon-request-${r.request_number}`}>
              <button onClick={() => loadMatches(r.request_number)} data-testid={`crm-moon-toggle-${r.request_number}`}
                className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left">
                <div>
                  <p className="text-sm font-bold text-navy-800">{r.request_number}</p>
                  <p className="text-xs text-graphite">{r.product} · {r.request_type === "location" ? "Location" : r.request_type === "piece_rare" ? "Pièce rare" : "Achat"}{r.quantity ? ` · qté ${r.quantity}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${MOON_BADGES[r.status] || "bg-slate-100 text-slate-600"}`}>{MOON_STATUS_LABELS[r.status] || r.status}</span>
                  {openReq === r.request_number ? <ChevronUp size={15} className="text-steel" /> : <ChevronDown size={15} className="text-steel" />}
                </div>
              </button>

              {openReq === r.request_number && (
                <div className="border-t border-line px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-steel">
                    Fournisseurs appariés {matches ? `(${matches.matches.length})` : "…"} — catégories déduites : {(matches?.inferred_categories || []).join(", ") || "…"}
                  </p>
                  {matches === null && <p className="mt-2 text-xs text-steel">Recherche des fournisseurs pertinents…</p>}
                  {matches && matches.matches.length === 0 && (
                    <p className="mt-2 text-xs text-amber-700">Aucun fournisseur apparié automatiquement — recherche manuelle requise.</p>
                  )}
                  {matches && matches.matches.slice(0, 8).map((m) => (
                    <div key={m.supplier_id} className="mt-2 rounded-xl border border-line p-3" data-testid={`crm-moon-match-${m.supplier_id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-navy-800">{m.supplier_name} <span className="ml-1 rounded-full bg-badge px-2 py-0.5 text-[10px] font-bold text-sky-700">score {m.score ?? "—"}</span></p>
                        <button onClick={() => solicit(r.request_number, m.supplier_id)} data-testid={`crm-moon-solicit-${m.supplier_id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 text-[11px] font-bold text-navy-800 hover:text-electric">
                          <Send size={11} /> Solliciter
                        </button>
                      </div>
                      <p className="mt-1 text-[11px] text-steel">{(m.reasons || []).join(" · ")}</p>
                    </div>
                  ))}
                  {matches?.manual_search_needed && (
                    <p className="mt-2 text-[11px] text-steel">Astuce : élargissez la recherche via l'onglet Fournisseurs du tableau de bord admin.</p>
                  )}

                  <p className="mt-4 text-[11px] font-bold uppercase tracking-wider text-steel">Fournisseurs sollicités ({r.inquiries.length})</p>
                  {r.inquiries.length === 0 && <p className="mt-1 text-xs text-steel">Aucun fournisseur sollicité pour le moment.</p>}
                  {r.inquiries.map((inq) => (
                    <div key={inq.id} className="mt-2 rounded-xl bg-paper p-3" data-testid={`crm-moon-inquiry-${inq.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-navy-800">{inq.supplier_name}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          {followUps[inq.id] != null && inq.follow_up_done_at == null && (
                            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`crm-followup-badge-${inq.id}`}>
                              Relance à faire ({followUps[inq.id]} j)
                            </span>
                          )}
                          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${INQUIRY_STATUS_COLORS[inq.status] || "bg-slate-100 text-slate-600"}`}>{INQUIRY_STATUS_LABELS[inq.status] || inq.status}</span>
                        </div>
                      </div>
                      <div className="mt-1 grid gap-x-4 gap-y-0.5 text-[11px] text-graphite sm:grid-cols-2">
                        <p>Prix : <span className="font-semibold">{inq.price ? `${inq.price} ${inq.currency || "CAD"}` : "—"}</span></p>
                        <p>Disponibilité : <span className="font-semibold">{inq.availability || "—"}</span></p>
                        <p>Délai : <span className="font-semibold">{inq.lead_time || "—"}</span></p>
                        <p>Transport : <span className="font-semibold">{inq.shipping_cost || "—"}</span></p>
                        <p>Validité de l'offre : <span className="font-semibold">{inq.offer_validity || "—"}</span></p>
                        <p>Prochaine relance : <span className="font-semibold">{inq.follow_up_due || "—"}</span></p>
                      </div>
                      {inq.comparison_notes && <p className="mt-1 text-[11px] text-graphite">Commentaires : {inq.comparison_notes}</p>}
                      {followUps[inq.id] != null && inq.follow_up_done_at == null && (
                        <button onClick={() => markFollowUpDone(inq)} data-testid={`crm-followup-done-${inq.id}`}
                          className="mt-2 rounded-full bg-amber-500 px-4 py-1.5 text-[11px] font-bold text-white hover:bg-amber-600">
                          Marquer la relance comme effectuée
                        </button>
                      )}
                      {inq.follow_up_done_at && (
                        <p className="mt-1 text-[11px] text-emerald-700">Relance effectuée le {new Date(inq.follow_up_done_at).toLocaleDateString("fr-CA")} par {inq.follow_up_done_by}</p>
                      )}
                      <div className="mt-2 flex items-center gap-3">
                        <button onClick={() => openEdit(inq)} data-testid={`crm-inq-edit-${inq.id}`} className="text-[11px] font-bold text-electric hover:underline">
                          Modifier la réponse
                        </button>
                        {(inq.edit_history || []).length > 0 && (
                          <details className="flex-1">
                            <summary className="cursor-pointer text-[11px] font-semibold text-steel hover:text-electric">Historique des modifications ({inq.edit_history.length})</summary>
                            <ul className="mt-1 space-y-1">
                              {inq.edit_history.slice().reverse().map((h, i) => (
                                <li key={i} className="rounded-lg bg-white px-2 py-1 text-[10px] text-steel" data-testid={`crm-inq-history-${inq.id}-${i}`}>
                                  {new Date(h.date).toLocaleString("fr-CA")} · {h.by} — {Object.keys(h.changes || {}).join(", ") || "aucun champ"}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                      {inq.status === "en_attente_validation" && (
                        <p className="mt-1 text-[11px] text-amber-700">Validez puis envoyez depuis l'onglet « Demandes de prix » du tableau de bord admin (validation humaine obligatoire).</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editInquiry && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-navy-950/50 p-4 backdrop-blur-sm" data-testid="crm-inq-edit-modal">
          <form onSubmit={saveEdit} className="my-8 w-full max-w-xl rounded-2xl bg-white p-8 shadow-card">
            <h3 className="font-display text-lg font-bold text-navy-950">Réponse de {editInquiry.supplier_name}</h3>
            <p className="mt-1 text-xs text-steel">{editInquiry.request_number} — auteur, date et historique des modifications sont enregistrés automatiquement.</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {[
                { key: "price", label: "Prix", type: "text" },
                { key: "currency", label: "Devise", type: "select", options: [["CAD", "CAD"], ["USD", "USD"]] },
                { key: "lead_time", label: "Délai de livraison", type: "text" },
                { key: "availability", label: "Disponibilité", type: "text" },
                { key: "shipping_cost", label: "Coût de transport", type: "text" },
                { key: "offer_validity", label: "Validité de l'offre", type: "text" },
                { key: "follow_up_due", label: "Prochaine relance", type: "date" },
              ].map((f) => (
                <div key={f.key}>
                  <label htmlFor={`inq-${f.key}`} className="mb-1 block text-[12px] font-bold text-navy-900">{f.label}</label>
                  {f.type === "select" ? (
                    <select id={`inq-${f.key}`} data-testid={`crm-inq-field-${f.key}`} value={editForm[f.key] || ""}
                      onChange={(e) => setEditForm((s) => ({ ...s, [f.key]: e.target.value }))}
                      className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm">
                      {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  ) : (
                    <input id={`inq-${f.key}`} type={f.type} data-testid={`crm-inq-field-${f.key}`} value={editForm[f.key] || ""}
                      onChange={(e) => setEditForm((s) => ({ ...s, [f.key]: e.target.value }))}
                      className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm" />
                  )}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label htmlFor="inq-notes" className="mb-1 block text-[12px] font-bold text-navy-900">Commentaires</label>
                <textarea id="inq-notes" rows={3} data-testid="crm-inq-field-comparison_notes" value={editForm.comparison_notes || ""}
                  onChange={(e) => setEditForm((s) => ({ ...s, comparison_notes: e.target.value }))}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditInquiry(null)} data-testid="crm-inq-edit-cancel"
                className="rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-graphite hover:text-navy-800">Annuler</button>
              <button type="submit" data-testid="crm-inq-edit-submit"
                className="rounded-full bg-electric px-6 py-2.5 text-sm font-bold text-white hover:bg-electric-dark">Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
